import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import { Form } from '@/components/ui-kit/form';
import { Button } from '@/components/ui-kit/button';
import { Accordion } from '@/components/ui-kit/accordion';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AgentBehaviorSettingsForm } from './utils';
import { MemorySection } from './memory-section/memory-section';
import { WelcomeGuideSection } from './welcome-guide-section/welcome-guide-section';
import { ToolsSection } from './agents-configuration-tools-section/tools-section';
import { KnowledgeBaseSection } from './knowledge-base-section/knowledge-base-section';
import { SafetyMechanismSection } from './safety-mechanism-section/safety-mechanism-section';
import { LlmSection } from './llm-section/llm-section';

type ConfigureCardProps = {
  form: UseFormReturn<AgentBehaviorSettingsForm>;
  agentId: string;
  isMobile: boolean;
  isSaving: boolean;
  lastSaveTime: Date | null;
  lastUpdatedDate?: string;
  onSubmit: (values: AgentBehaviorSettingsForm) => Promise<void>;
  onShowPreview: () => void;
  onOpenBasePromptAIModal: () => void;
  showPreviewButton?: boolean;
};

export const AgentConfigCardForm = ({
  form,
  agentId,
  isMobile,
  showPreviewButton = false,
  onSubmit,
  onShowPreview,
  onOpenBasePromptAIModal,
}: ConfigureCardProps) => {
  const { t } = useTranslation();
  const { control, handleSubmit } = form;

  return (
    <Card className={`flex h-full flex-col px-0 pb-0`}>
      <CardHeader className="flex-row items-center justify-between px-4">
        <CardTitle>{t('CONFIGURE_YOUR_AGENT')}</CardTitle>

        {(isMobile || showPreviewButton) && (
          <Button variant="outline" size="sm" onClick={onShowPreview}>
            {t('PREVIEW')}
          </Button>
        )}
      </CardHeader>

      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col overflow-hidden">
          <CardContent
            className="flex-1 overflow-y-auto py-4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgb(156 163 175) transparent',
            }}
          >
            <Accordion type="single" collapsible>
              <LlmSection
                form={form}
                isMobile={isMobile}
                onOpenBasePromptAIModal={onOpenBasePromptAIModal}
                onModelSubmit={onSubmit}
              />

              <KnowledgeBaseSection form={form} isMobile={isMobile} onSubmit={onSubmit} />

              <ToolsSection agentId={agentId} />

              <MemorySection
                control={control}
                form={form}
                isMobile={isMobile}
                onSubmit={onSubmit}
              />

              <WelcomeGuideSection
                control={control}
                form={form}
                isMobile={isMobile}
                agentId={agentId}
                onSubmit={onSubmit}
              />

              <SafetyMechanismSection form={form} isMobile={isMobile} onModelSubmit={onSubmit} />
            </Accordion>
          </CardContent>
        </form>
      </Form>
    </Card>
  );
};
